import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Search, Plus, Filter, ChevronRight, Upload } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import BulkImportDialog from "../components/BulkImportDialog";

const stageBadge = {
  active: { bg: "bg-[#E8F0E9]", text: "text-emerald-600" },
  vip: { bg: "bg-rose-50", text: "text-slate-900" },
  lead: { bg: "bg-[#EAF0F6]", text: "text-blue-600" },
  "at-risk": { bg: "bg-[#FDF3E1]", text: "text-amber-600" },
  churned: { bg: "bg-rose-50", text: "text-rose-600" },
  "first-visit": { bg: "bg-[#EAF0F6]", text: "text-blue-600" },
  consultation: { bg: "bg-[#FAFAFA]", text: "text-slate-600" },
};

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [filterStage, setFilterStage] = useState("");
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [newClient, setNewClient] = useState({ first_name: "", last_name: "", email: "", phone: "", pipeline_stage: "active" });
  const navigate = useNavigate();

  const fetchClients = async () => {
    try {
      let url = `/clients?limit=100`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (filterStage && filterStage !== "all") url += `&pipeline_stage=${filterStage}`;
      const res = await api.get(url);
      setClients(res.data.clients || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [search, filterStage]);

  const handleCreate = async () => {
    try {
      await api.post("/clients", newClient);
      setShowNew(false);
      setNewClient({ first_name: "", last_name: "", email: "", phone: "", pipeline_stage: "active" });
      fetchClients();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="clients-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8 animate-fadeIn">
        <div>
          <h1 className="font-['Outfit'] text-3xl font-light tracking-tight text-slate-900">Clients</h1>
          <p className="text-slate-600 text-sm mt-1">{total} total clients</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="import-clients-btn"
            variant="outline"
            onClick={() => setShowImport(true)}
            className="border-slate-200 rounded-full h-10 px-5 text-sm"
          >
            <Upload className="w-4 h-4 mr-1.5" /> Import CSV
          </Button>
          <Button
            data-testid="new-client-btn"
            onClick={() => setShowNew(true)}
            className="bg-slate-900 text-white hover:bg-slate-800 rounded-full h-10 px-5 text-sm"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Add Client
          </Button>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="flex gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="client-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search clients..."
            className="pl-10 h-10 bg-white border-slate-200 rounded-lg"
          />
        </div>
        <Select value={filterStage} onValueChange={setFilterStage}>
          <SelectTrigger data-testid="stage-filter" className="w-[160px] h-10 border-slate-200 bg-white rounded-lg text-sm">
            <Filter className="w-3.5 h-3.5 mr-1 text-slate-400" />
            <SelectValue placeholder="All stages" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stages</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="vip">VIP</SelectItem>
            <SelectItem value="lead">Lead</SelectItem>
            <SelectItem value="at-risk">At Risk</SelectItem>
            <SelectItem value="churned">Churned</SelectItem>
            <SelectItem value="first-visit">First Visit</SelectItem>
            <SelectItem value="consultation">Consultation</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Client List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-[#FAFAFA] border-b border-slate-200">
                <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Client</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Contact</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Stage</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Visits</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">LTV</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Last Visit</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {clients.map((client, idx) => {
                const badge = stageBadge[client.pipeline_stage] || stageBadge.active;
                return (
                  <tr
                    key={client.client_id}
                    data-testid={`client-row-${client.client_id}`}
                    className="border-b border-slate-200 hover:bg-[#FAFAFA] cursor-pointer transition-colors"
                    onClick={() => navigate(`/clients/${client.client_id}`)}
                    style={{ animationDelay: `${idx * 30}ms` }}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-rose-50 flex items-center justify-center text-sm font-medium text-slate-900">
                          {client.first_name?.[0]}{client.last_name?.[0]}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-900">{client.first_name} {client.last_name}</p>
                          <div className="flex gap-1 mt-0.5">
                            {(client.tags || []).slice(0, 2).map(tag => (
                              <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-[#FAFAFA] text-slate-400">{tag}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm text-slate-600">{client.email}</p>
                      <p className="text-xs text-slate-400">{client.phone}</p>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                        {client.pipeline_stage}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-900 font-medium">{client.visit_count || 0}</td>
                    <td className="px-4 py-4 text-sm text-slate-900 font-medium">${(client.lifetime_value || 0).toFixed(0)}</td>
                    <td className="px-4 py-4 text-sm text-slate-400">
                      {client.last_visit ? new Date(client.last_visit).toLocaleDateString() : "Never"}
                    </td>
                    <td className="px-4 py-4">
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </td>
                  </tr>
                );
              })}
              {clients.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-sm text-slate-400">
                    No clients found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* New Client Modal */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="sm:max-w-[440px] bg-white border-slate-200 rounded-2xl p-8" data-testid="new-client-modal">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl font-medium">New Client</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1.5 block">First Name</label>
                <Input
                  data-testid="new-client-first-name"
                  value={newClient.first_name}
                  onChange={(e) => setNewClient(p => ({ ...p, first_name: e.target.value }))}
                  className="h-11 border-slate-200 bg-white rounded-lg"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1.5 block">Last Name</label>
                <Input
                  data-testid="new-client-last-name"
                  value={newClient.last_name}
                  onChange={(e) => setNewClient(p => ({ ...p, last_name: e.target.value }))}
                  className="h-11 border-slate-200 bg-white rounded-lg"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600 mb-1.5 block">Email</label>
              <Input
                data-testid="new-client-email"
                type="email"
                value={newClient.email}
                onChange={(e) => setNewClient(p => ({ ...p, email: e.target.value }))}
                className="h-11 border-slate-200 bg-white rounded-lg"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600 mb-1.5 block">Phone</label>
              <Input
                data-testid="new-client-phone"
                value={newClient.phone}
                onChange={(e) => setNewClient(p => ({ ...p, phone: e.target.value }))}
                className="h-11 border-slate-200 bg-white rounded-lg"
              />
            </div>
            <Button
              data-testid="create-client-btn"
              onClick={handleCreate}
              className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-full h-11"
            >
              Add Client
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <BulkImportDialog open={showImport} onOpenChange={setShowImport} type="clients" onDone={fetchClients} />
    </div>
  );
}
